'use client';

import { ImagePlus, Loader2, Search, Sparkles, Sticker, Upload } from 'lucide-react';
import { useMemo, useRef, useState, type ChangeEvent, type CSSProperties, type ReactNode } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { makeId } from '@/lib/editor/editorClips';
import { useEditorStore } from '@/lib/editor/editorStore';
import { BADGE_PRESETS, type TextPreset } from '@/lib/editor/textPresets';
import { STICKER_PACKS } from '@/lib/editor/stickerPacks';
import { getLayerFontFamily } from '@/lib/editor/fonts';
import { useAttachmentUpload } from '../hooks/useAttachmentUpload';

function badgePreviewStyle(preset: TextPreset): CSSProperties {
  const radius = preset.badgeStyle === 'pill' ? 999 : preset.badgeStyle === 'soft-square' ? 9 : 15;
  return {
    color: preset.color,
    background: preset.badgeAccentColor
      ? `linear-gradient(135deg, ${preset.backgroundColor} 0%, ${preset.badgeAccentColor} 100%)`
      : preset.backgroundColor,
    borderColor: preset.badgeBorderColor,
    borderWidth: preset.badgeBorderWidth ? Math.max(1, preset.badgeBorderWidth * 0.55) : 0,
    borderRadius: radius,
    fontFamily: getLayerFontFamily(preset.fontFamily),
    fontWeight: preset.fontWeight,
    fontStyle: preset.fontStyle,
  };
}

export function StickersPanel() {
  const { t } = useI18n();
  const assets = useEditorStore((s) => s.assets);
  const addAsset = useEditorStore((s) => s.addAsset);
  const addStickerLayer = useEditorStore((s) => s.addStickerLayer);
  const addPresetLayer = useEditorStore((s) => s.addPresetLayer);
  const setActiveTool = useEditorStore((s) => s.setActiveTool);
  const { uploadImage } = useAttachmentUpload();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activePack, setActivePack] = useState('all');
  const [query, setQuery] = useState('');

  const visiblePacks = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return STICKER_PACKS
      .filter((pack) => activePack === 'all' || pack.id === activePack)
      .map((pack) => ({
        ...pack,
        stickers: pack.stickers.filter((item) =>
          normalizedQuery ? item.title.toLowerCase().includes(normalizedQuery) : true
        ),
      }))
      .filter((pack) => pack.stickers.length > 0);
  }, [activePack, query]);

  const uploadSticker = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const src = await uploadImage(file);
      const asset = {
        id: makeId('sticker-asset'),
        title: file.name.replace(/\.[^/.]+$/, ''),
        src,
        mimeType: file.type || 'image/png',
        createdAt: new Date().toISOString(),
      };
      addAsset(asset);
      addStickerLayer(asset);
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Не удалось загрузить стикер');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-[#121214] text-white">
      <div className="flex h-10 shrink-0 items-center border-b border-white/10 px-3.5">
        <p className="text-sm font-semibold">{t('studio.editor.toolStickers')}</p>
      </div>

      <div data-lenis-prevent className="min-h-0 flex-1 overflow-y-auto p-3 pb-8">
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={uploadSticker} />
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-brand-600 text-xs font-bold transition hover:bg-brand-500 disabled:opacity-60"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Загрузить PNG, WebP или GIF
        </button>
        {error && <p className="mt-2 rounded-lg bg-red-500/10 p-2 text-[11px] text-red-300">{error}</p>}

        <label className="mt-3 flex h-9 items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-2.5 focus-within:border-brand-500">
          <Search className="h-3.5 w-3.5 text-white/35" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Найти стикер"
            className="min-w-0 flex-1 bg-transparent text-xs text-white outline-none placeholder:text-white/30"
          />
        </label>

        <div data-lenis-prevent className="mt-3 flex gap-1.5 overflow-x-auto pb-1">
          <PackPill active={activePack === 'all'} onClick={() => setActivePack('all')}>Все</PackPill>
          {STICKER_PACKS.map((pack) => (
            <PackPill key={pack.id} active={activePack === pack.id} onClick={() => setActivePack(pack.id)}>
              {pack.name}
            </PackPill>
          ))}
        </div>

        {visiblePacks.length ? (
          <div className="mt-3 space-y-4">
            {visiblePacks.map((pack) => (
              <section key={pack.id} className={`rounded-2xl border border-white/10 bg-gradient-to-br ${pack.accent} p-2.5`}>
                <div className="mb-2 flex items-end justify-between gap-2">
                  <div>
                    <h3 className="text-xs font-black text-white/90">{pack.name}</h3>
                    <p className="mt-0.5 text-[9px] text-white/40">{pack.description}</p>
                  </div>
                  <span className="rounded-full bg-black/20 px-2 py-0.5 text-[9px] font-bold text-white/50">
                    {pack.stickers.length}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  {pack.stickers.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      title={item.title}
                      onClick={() => addStickerLayer(item)}
                      className="group relative aspect-square overflow-hidden rounded-xl border border-white/10 bg-[linear-gradient(45deg,#17121f_25%,#211a2c_25%,#211a2c_50%,#17121f_50%,#17121f_75%,#211a2c_75%)] bg-[length:12px_12px] p-1.5 transition hover:-translate-y-0.5 hover:border-brand-400 hover:shadow-[0_8px_24px_rgba(91,53,255,.28)]"
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={item.src}
                        alt={item.title}
                        draggable={false}
                        className="h-full w-full object-contain transition duration-200 group-hover:scale-110"
                      />
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="mt-3 rounded-xl border border-dashed border-white/15 px-3 py-5 text-center text-[11px] text-white/40">
            Ничего не найдено
          </div>
        )}

        <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.025] p-2.5">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white/50">
              <Sparkles className="h-3 w-3 text-brand-300" /> Текстовые бейджи
            </div>
            <span className="text-[9px] text-white/30">Можно изменить текст</span>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2">
          {BADGE_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              onClick={() => {
                addPresetLayer(preset);
                setActiveTool('stickers');
              }}
              className="group flex h-[76px] items-center justify-center rounded-xl border border-white/10 bg-black/20 px-2 transition hover:-translate-y-0.5 hover:border-brand-400/70 hover:bg-white/5"
            >
              <span
                className="inline-flex max-w-full items-center justify-center border-solid px-3.5 py-1.5 text-[13px] leading-none shadow-[0_8px_20px_rgba(0,0,0,.3)] transition group-hover:scale-105"
                style={badgePreviewStyle(preset)}
              >
                {preset.name}
              </span>
            </button>
          ))}
          </div>
          <p className="mt-2 text-[9px] leading-4 text-white/30">
            Добавьте бейдж и дважды кликните по нему на холсте, чтобы изменить надпись.
          </p>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white/40">
            <ImagePlus className="h-3 w-3" /> Мои стикеры
          </p>
          <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] text-white/50">{assets.length}</span>
        </div>

        {assets.length ? (
          <div className="mt-2 grid grid-cols-2 gap-2">
            {assets.map((asset) => (
              <button
                key={asset.id}
                type="button"
                onClick={() => addStickerLayer(asset)}
                className="overflow-hidden rounded-xl border border-white/10 bg-white/5 text-left transition hover:border-brand-500"
              >
                <div className="flex aspect-square items-center justify-center bg-[linear-gradient(45deg,#17121f_25%,#211a2c_25%,#211a2c_50%,#17121f_50%,#17121f_75%,#211a2c_75%)] bg-[length:14px_14px] p-2">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={asset.src} alt={asset.title} className="h-full w-full object-contain" />
                </div>
                <p className="truncate px-2 py-1.5 text-[11px] font-semibold text-white/70">{asset.title}</p>
              </button>
            ))}
          </div>
        ) : (
          <div className="mt-2 flex flex-col items-center gap-2 rounded-xl border border-dashed border-white/15 px-3 py-5 text-center">
            <Sticker className="h-5 w-5 text-white/25" />
            <p className="text-[11px] leading-5 text-white/40">Загрузите свой стикер или добавьте фото из раздела «Стоки»</p>
          </div>
        )}
      </div>
    </div>
  );
}

function PackPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-bold transition ${
        active
          ? 'border-brand-400 bg-brand-600/35 text-white'
          : 'border-white/10 bg-white/5 text-white/50 hover:border-white/25 hover:text-white/80'
      }`}
    >
      {children}
    </button>
  );
}
